/**
 * ユーザー管理ページ
 *
 * SYSTEM_ADMIN: 全施設のユーザーを管理
 * FACILITY_ADMIN: 自施設のEXAMINERのみ管理
 */

import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useApi, useMutation } from "../hooks/useApi";
import { api } from "../services/api";
import type {
  CreateUserRequest,
  Facility,
  ResetPasswordRequest,
  UpdateUserRequest,
  User,
  UserRole,
} from "../types";

// ロールの表示名とバッジカラー
const ROLE_CONFIG: Record<UserRole, { label: string; className: string }> = {
  SYSTEM_ADMIN: {
    label: "システム管理者",
    className: "bg-purple-100 text-purple-800",
  },
  FACILITY_ADMIN: {
    label: "施設管理者",
    className: "bg-blue-100 text-blue-800",
  },
  EXAMINER: {
    label: "検査者",
    className: "bg-green-100 text-green-800",
  },
};

/**
 * ロールバッジコンポーネント
 */
function RoleBadge({ role }: { role: UserRole }) {
  const config = ROLE_CONFIG[role];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
}

/**
 * ステータスバッジコンポーネント
 */
function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
      }`}
    >
      {isActive ? "有効" : "無効"}
    </span>
  );
}

/**
 * 新規ユーザー作成フォーム
 */
function CreateUserForm({
  currentUser,
  facilities,
  onSuccess,
  onCancel,
}: {
  currentUser: User;
  facilities: Facility[] | null;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("EXAMINER");
  const [facilityId, setFacilityId] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useMutation<User, CreateUserRequest>(
    "/api/users",
    "POST",
  );

  // FACILITY_ADMINの場合はEXAMINERに固定
  const availableRoles: UserRole[] =
    currentUser.role === "SYSTEM_ADMIN"
      ? ["SYSTEM_ADMIN", "FACILITY_ADMIN", "EXAMINER"]
      : ["EXAMINER"];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!navigator.onLine) {
      setFormError("現在オフラインのため、ユーザー編集はできません。");
      return;
    }

    if (!navigator.onLine) {
      setFormError("現在オフラインのため、ユーザー新規登録はできません。");
      return;
    }

    // バリデーション
    if (!name.trim()) {
      setFormError("氏名を入力してください");
      return;
    }
    if (!email.trim()) {
      setFormError("メールアドレスを入力してください");
      return;
    }
    if (password.length < 8) {
      setFormError("パスワードは8文字以上で入力してください");
      return;
    }
    if (
      currentUser.role === "SYSTEM_ADMIN" &&
      role !== "SYSTEM_ADMIN" &&
      !facilityId
    ) {
      setFormError("施設を選択してください");
      return;
    }

    const requestData: CreateUserRequest = {
      email: email.trim(),
      password,
      name: name.trim(),
      role,
      facilityId:
        currentUser.role === "SYSTEM_ADMIN"
          ? role === "SYSTEM_ADMIN"
            ? null
            : facilityId
          : undefined,
    };

    const result = await createMutation.mutate(requestData);
    if (result) {
      onSuccess();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">
        新規ユーザー登録
      </h2>

      {(formError || createMutation.error) && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
          <p className="text-red-600 text-sm">
            {formError || createMutation.error}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 氏名 */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              氏名 <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="山田 太郎"
            />
          </div>

          {/* メールアドレス */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              メールアドレス <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="user@example.com"
            />
          </div>

          {/* パスワード */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              パスワード <span className="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="8文字以上"
            />
          </div>

          {/* ロール */}
          <div>
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              ロール <span className="text-red-500">*</span>
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {availableRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_CONFIG[r].label}
                </option>
              ))}
            </select>
          </div>

          {/* 施設選択（SYSTEM_ADMINかつSYSTEM_ADMIN以外のロールの場合） */}
          {currentUser.role === "SYSTEM_ADMIN" && role !== "SYSTEM_ADMIN" && (
            <div>
              <label
                htmlFor="facilityId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                所属施設 <span className="text-red-500">*</span>
              </label>
              <select
                id="facilityId"
                value={facilityId}
                onChange={(e) => setFacilityId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">施設を選択</option>
                {facilities?.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name} ({f.code})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* ボタン */}
        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={createMutation.loading}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {createMutation.loading ? "作成中..." : "作成"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * ユーザー編集モーダル
 */
function EditUserModal({
  targetUser,
  currentUser,
  onSuccess,
  onClose,
}: {
  targetUser: User;
  currentUser: User;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(targetUser.name);
  const [email, setEmail] = useState(targetUser.email);
  const [role, setRole] = useState<UserRole>(targetUser.role);
  const [formError, setFormError] = useState<string | null>(null);

  const updateMutation = useMutation<User, UpdateUserRequest>(
    `/api/users/${targetUser.id}`,
    "PUT",
  );

  const isSystemAdmin = currentUser.role === "SYSTEM_ADMIN";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!navigator.onLine) {
      setFormError("現在オフラインのため、パスワードリセットはできません。");
      return;
    }

    if (!name.trim()) {
      setFormError("氏名を入力してください");
      return;
    }
    if (!email.trim()) {
      setFormError("メールアドレスを入力してください");
      return;
    }

    const updateData: UpdateUserRequest = {};
    if (name.trim() !== targetUser.name) updateData.name = name.trim();
    if (email.trim() !== targetUser.email) updateData.email = email.trim();
    if (isSystemAdmin && role !== targetUser.role) updateData.role = role;

    // 変更がない場合
    if (Object.keys(updateData).length === 0) {
      onClose();
      return;
    }

    const result = await updateMutation.mutate(updateData);
    if (result) {
      onSuccess();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          ユーザー編集
        </h2>

        {(formError || updateMutation.error) && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
            <p className="text-red-600 text-sm">
              {formError || updateMutation.error}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 氏名 */}
          <div>
            <label
              htmlFor="edit-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              氏名
            </label>
            <input
              id="edit-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* メールアドレス */}
          <div>
            <label
              htmlFor="edit-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              メールアドレス
            </label>
            <input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* ロール（SYSTEM_ADMINのみ変更可能） */}
          {isSystemAdmin && (
            <div>
              <label
                htmlFor="edit-role"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                ロール
              </label>
              <select
                id="edit-role"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="SYSTEM_ADMIN">
                  {ROLE_CONFIG.SYSTEM_ADMIN.label}
                </option>
                <option value="FACILITY_ADMIN">
                  {ROLE_CONFIG.FACILITY_ADMIN.label}
                </option>
                <option value="EXAMINER">{ROLE_CONFIG.EXAMINER.label}</option>
              </select>
            </div>
          )}

          {/* ボタン */}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={updateMutation.loading}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {updateMutation.loading ? "更新中..." : "更新"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * パスワードリセットモーダル
 */
function ResetPasswordModal({
  targetUser,
  onSuccess,
  onClose,
}: {
  targetUser: User;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const resetMutation = useMutation<{ message: string }, ResetPasswordRequest>(
    `/api/users/${targetUser.id}/reset-password`,
    "PATCH",
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (newPassword.length < 8) {
      setFormError("パスワードは8文字以上で入力してください");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError("パスワードが一致しません");
      return;
    }

    const result = await resetMutation.mutate({ newPassword });
    if (result) {
      onSuccess();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          パスワードリセット
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {targetUser.name} さんのパスワードをリセットします
        </p>

        {(formError || resetMutation.error) && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
            <p className="text-red-600 text-sm">
              {formError || resetMutation.error}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="new-password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              新しいパスワード
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="8文字以上"
            />
          </div>

          <div>
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              パスワード確認
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="もう一度入力"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={resetMutation.loading}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {resetMutation.loading ? "リセット中..." : "リセット"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * ユーザー管理ページ メインコンポーネント
 */
function UsersPage() {
  const { user: currentUser } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [facilityFilter, setFacilityFilter] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ユーザー一覧取得
  const apiEndpoint =
    currentUser?.role === "SYSTEM_ADMIN" && facilityFilter
      ? `/api/users?facilityId=${facilityFilter}`
      : "/api/users";
  const {
    data: users,
    loading,
    error,
    refetch,
  } = useApi<User[]>(apiEndpoint, [facilityFilter]);

  // 施設一覧取得（SYSTEM_ADMINの場合のみ）
  const { data: facilities } = useApi<Facility[]>(
    currentUser?.role === "SYSTEM_ADMIN" ? "/api/facilities" : "",
  );

  // 成功メッセージの自動消去
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (!currentUser) return null;

  // 無効化/有効化ハンドラ
  const handleToggleActive = async (targetUser: User) => {
    if (targetUser.id === currentUser.id) {
      return;
    }

    if (!navigator.onLine) {
      window.alert(
        "現在オフラインのため、ユーザーの有効/無効を変更できません。",
      );
      return;
    }

    const action = targetUser.isActive ? "無効化" : "有効化";
    if (!window.confirm(`${targetUser.name} さんを${action}しますか?`)) {
      return;
    }

    try {
      if (targetUser.isActive) {
        await api.delete(`/api/users/${targetUser.id}`);
      } else {
        await api.put(`/api/users/${targetUser.id}`, { isActive: true });
      }
      setSuccessMessage(`${targetUser.name} さんを${action}しました`);
      refetch();
    } catch {
      setSuccessMessage(null);
    }
  };

  // ローディング
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  // エラー
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-red-800 font-semibold mb-2">
          エラーが発生しました
        </h2>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          再試行
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* 成功メッセージ */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-800 text-sm">{successMessage}</p>
        </div>
      )}

      {/* ページヘッダー */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ユーザー管理</h1>
        <div className="flex w-full md:w-auto flex-wrap md:flex-nowrap items-center gap-2 md:gap-3">
          {/* 施設フィルタ（SYSTEM_ADMINのみ） */}
          {currentUser.role === "SYSTEM_ADMIN" && facilities && (
            <select
              value={facilityFilter}
              onChange={(e) => setFacilityFilter(e.target.value)}
              className="w-full md:w-auto px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全施設</option>
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex-1 md:flex-none px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            新規登録
          </button>
        </div>
      </div>

      {/* 新規作成フォーム */}
      {showCreateForm && (
        <CreateUserForm
          currentUser={currentUser}
          facilities={facilities}
          onSuccess={() => {
            setShowCreateForm(false);
            setSuccessMessage("ユーザーを作成しました");
            refetch();
          }}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* ユーザー一覧テーブル */}
      {!users || users.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
          ユーザーが登録されていません
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[14%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  氏名
                </th>
                <th className="w-[22%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  メールアドレス
                </th>
                <th className="w-[14%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ロール
                </th>
                {currentUser.role === "SYSTEM_ADMIN" && (
                  <th className="hidden xl:table-cell w-[16%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    所属施設
                  </th>
                )}
                <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状態
                </th>
                <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  登録日
                </th>
                <th className="w-[14%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  最終ログイン
                </th>
                <th className="w-[20%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((u) => (
                <tr
                  key={u.id}
                  className={`hover:bg-gray-50 ${!u.isActive ? "opacity-60" : ""}`}
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {u.name}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600 truncate">
                      {u.email}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <RoleBadge role={u.role} />
                  </td>
                  {currentUser.role === "SYSTEM_ADMIN" && (
                    <td className="hidden xl:table-cell px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 truncate">
                        {u.facility ? `${u.facility.name}` : "-"}
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <StatusBadge isActive={u.isActive} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(u.createdAt).toLocaleDateString("ja-JP")}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {u.lastLoginAt ? (
                        <>
                          <span className="xl:hidden">
                            {new Date(u.lastLoginAt).toLocaleDateString(
                              "ja-JP",
                            )}
                          </span>
                          <span className="hidden xl:inline">
                            {new Date(u.lastLoginAt).toLocaleString("ja-JP")}
                          </span>
                        </>
                      ) : (
                        "-"
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                      {/* 編集ボタン */}
                      <button
                        onClick={() => {
                          if (!navigator.onLine) {
                            window.alert(
                              "現在オフラインのため、ユーザー編集はできません。",
                            );
                            return;
                          }
                          setEditingUser(u);
                        }}
                        className="px-2 py-1 rounded border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                        title="編集"
                      >
                        編集
                      </button>

                      {/* パスワードリセットボタン */}
                      <button
                        onClick={() => {
                          if (!navigator.onLine) {
                            window.alert(
                              "現在オフラインのため、パスワードリセットはできません。",
                            );
                            return;
                          }
                          setResetPasswordUser(u);
                        }}
                        className="px-2 py-1 rounded border border-yellow-200 bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-800 transition-colors"
                        title="パスワードリセット"
                      >
                        PW
                      </button>

                      {/* 無効化/有効化ボタン（自分自身は不可） */}
                      {u.id !== currentUser.id && (
                        <button
                          onClick={() => handleToggleActive(u)}
                          className={`transition-colors ${
                            u.isActive
                              ? "px-2 py-1 rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800"
                              : "px-2 py-1 rounded border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-800"
                          }`}
                          title={u.isActive ? "無効化" : "有効化"}
                        >
                          {u.isActive ? "無効化" : "有効化"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 件数表示 */}
      {users && users.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-right">
          全 {users.length} 件
        </div>
      )}

      {/* 編集モーダル */}
      {editingUser && (
        <EditUserModal
          targetUser={editingUser}
          currentUser={currentUser}
          onSuccess={() => {
            setEditingUser(null);
            setSuccessMessage("ユーザー情報を更新しました");
            refetch();
          }}
          onClose={() => setEditingUser(null)}
        />
      )}

      {/* パスワードリセットモーダル */}
      {resetPasswordUser && (
        <ResetPasswordModal
          targetUser={resetPasswordUser}
          onSuccess={() => {
            setResetPasswordUser(null);
            setSuccessMessage("パスワードをリセットしました");
          }}
          onClose={() => setResetPasswordUser(null)}
        />
      )}
    </div>
  );
}

export default UsersPage;
